import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import useSettings from '@/hooks/useSettings'
import InvoicesService from '@/services/InvoicesService'
import SettingsService from '@/services/SettingsService'

import useInvoiceFormState from '../common/useInvoiceFormState'
import NotesSection from '../common/NotesSection'
import DateDiscountTermSection from '../common/DateDiscountTermSection'
import PlacowkaPicker from '@/widgets/PlacowkaPicker'
import PlacowkaCard from '@/widgets/PlacowkaCard'
import InfaktCustomerPicker from '@/widgets/InfaktCustomerPicker'
import ProductTable from '../common/ProductTable'
import TotalsSection from '../common/TotalsSection'
import { appendInfaktCustomerToPayload, buildPayload, syncProductsToCatalog } from '../common/submitInvoice'

export default function InvoiceAdd() {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { settings } = useSettings()
	const currencySymbol = settings?.companyCurrencySymbol?.trim() || 'AED'
	const fmtMoney = n => `${currencySymbol} ${Number(n ?? 0).toFixed(2)}`

	const { form, setField, setProduct, addRow, removeRow } = useInvoiceFormState()
	const [error, setError] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [placowkaData, setPlacowkaData] = useState(null)
	const [infaktCustomer, setInfaktCustomer] = useState(null)
	const [systemSettings, setSystemSettings] = useState(null)
	const [selectedCompanyId, setSelectedCompanyId] = useState('')

	useEffect(() => {
		SettingsService.getSystem().then(({ data }) => {
			if (data) setSystemSettings(data)
		})
	}, [])

	const companies = useMemo(() => {
		if (!Array.isArray(systemSettings?.companies)) return []
		return systemSettings.companies
			.filter(c => c?.id)
			.map(c => ({ id: c.id, name: c.companyName || c.id }))
			.sort((a, b) => a.name.localeCompare(b.name))
	}, [systemSettings])

	useEffect(() => {
		if (companies.length === 1 && !selectedCompanyId) {
			setSelectedCompanyId(companies[0].id)
		}
	}, [companies, selectedCompanyId])

	const handlePlacowkaSelect = useCallback(selectedPlacowka => {
		setPlacowkaData(selectedPlacowka)
		const id = String(selectedPlacowka?._id ?? selectedPlacowka?.id ?? '')
		const name = (selectedPlacowka?.nazwa || '').trim()
		setField('customerId', id)
		setField('customerName', name)
	}, [setField])

	const handlePlacowkaClear = useCallback(() => {
		setPlacowkaData(null)
		setField('customerId', '')
		setField('customerName', '')
	}, [setField])

	const handleInfaktCustomerChange = useCallback(customerData => {
		setInfaktCustomer(customerData)
	}, [])

	const handleSave = useCallback(async e => {
		e?.preventDefault()
		if (submitting) return
		setSubmitting(true)
		setError(null)

		try {
			const { success: syncOk, error: syncError } = await syncProductsToCatalog(form.products)
			if (!syncOk) throw new Error(syncError || t('invoices.add.messages.createFailed'))

			const payload = buildPayload(form)
			if (selectedCompanyId) payload.companyId = selectedCompanyId
			appendInfaktCustomerToPayload(payload, infaktCustomer)
			if (form.dueDate) payload.dueDate = form.dueDate
			const { success, error: apiError, data } = await InvoicesService.createInvoice(payload)
			if (!success) throw new Error(apiError || t('invoices.add.messages.createFailed'))

			navigate(`/ksiegowosc/invoices/details/${data._id}`)
		} catch (err) {
			setError(err.message || t('invoices.add.messages.createFailed'))
			await Swal.fire({
				icon: 'error',
				title: t('invoices.add.messages.errorTitle'),
				text: err.message || t('invoices.add.messages.createFailed')
			})
		} finally {
			setSubmitting(false)
		}
	}, [form, infaktCustomer, navigate, selectedCompanyId, submitting, t])

	return (
		<div className="max-w-6xl mx-auto px-0 sm:px-6 lg:px-8">
			<form onSubmit={handleSave} className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				<div className="px-4 sm:px-6 py-6 border-b border-gray-200">
					<h2 className="text-2xl font-bold text-gray-900">{t('invoices.add.title')}</h2>
					<p className="text-sm text-gray-500 mt-1">{placowkaData?.nazwa || '-'}</p>
				</div>

				{error && (<div className="mx-4 sm:mx-6 mt-6 rounded-lg bg-red-50 p-4 border border-red-200 text-sm text-red-700"> {error} </div> )}

				<div className="px-0 sm:px-6 py-6 space-y-6">

					{/* Company */}
					{companies.length > 0 && (
						<div className="px-4 sm:px-0">
							<label className="block text-sm font-medium text-gray-700 mb-1">Firma</label>
							<select
								value={selectedCompanyId}
								onChange={e => setSelectedCompanyId(e.target.value)}
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 text-sm"
							>
								<option value="">— Wybierz firmę —</option>
								{companies.map(c => (
									<option key={c.id} value={c.id}>{c.name}</option>
								))}
							</select>
						</div>
					)}

					{(companies.length === 0 || selectedCompanyId) && (
						<>
							{/* Placowka */}
							{!placowkaData && (
								<PlacowkaPicker onPlacowkaSelect={handlePlacowkaSelect} />
							)}
							{placowkaData && (
								<PlacowkaCard
									placowkaData={placowkaData}
									handleChange={handlePlacowkaClear}
								/>
							)}

							{/* Infakt Customer */}
							<InfaktCustomerPicker
								onCustomerSelect={handleInfaktCustomerChange}
								value={infaktCustomer}
								companyId={selectedCompanyId}
								placowkaData={placowkaData}
							/>

							{/* Date / Discount / Term */}
							<DateDiscountTermSection form={form} setField={setField}/>

							<ProductTable products={form.products} setProduct={setProduct} addRow={addRow} removeRow={removeRow} fmtMoney={fmtMoney}/>

							<NotesSection form={form} setField={setField} />

							<TotalsSection form={form} fmtMoney={fmtMoney} />
						</>
					)}

				</div>

				<div className="px-4 sm:px-6 py-6 border-t border-gray-200 flex gap-3">
					{(companies.length === 0 || selectedCompanyId) && (
						<button
							type="submit"
							disabled={submitting}
							className={`px-5 py-2 rounded text-white ${submitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
							{submitting ? t('invoices.add.submitting') : t('invoices.add.submit')}
						</button>
					)}

					<button
						type="button"
						onClick={() => navigate('/ksiegowosc/invoices')}
						className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50">
						{t('invoices.add.cancel')}
					</button>
				</div>
			</form>
		</div>
	)
}
